import { useState } from "react";
import {
  Paper,
  Group,
  Text,
  Badge,
  Button,
  Stack,
  Divider,
  Tooltip,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconMusic,
  IconBan,
  IconRefresh,
  IconRotate,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import { Endpoints } from "../../lib/endpoints";
import { usePerformRequest } from "../../hooks/usePerformRequest";
import { AdminPermissions } from "../../lib/data";
import PermissionGuard from "../PermissionGuard";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("t")}` },
});

const tierColor = (t?: string | null) =>
  t === "verified" ? "green" : t === "streaming" ? "yellow" : "gray";
const statusColor = (s?: string) =>
  s === "verified"
    ? "green"
    : s === "streaming"
      ? "yellow"
      : s === "rejected"
        ? "red"
        : s === "manual_review"
          ? "orange"
          : "gray";
const overrideColor = (o?: string) =>
  o === "force_verified"
    ? "green"
    : o === "force_streaming"
      ? "yellow"
      : o === "banned"
        ? "red"
        : "gray";

interface Props {
  /** Member id or email. */
  userId: string;
}

/**
 * Verification & tier panel for a single member — shows their computed tier,
 * linked socials + verdicts, and lets an admin override (rescue a false
 * positive, gate to streaming, or ban) or recompute from the socials.
 */
function MemberVerificationInner({ userId }: Props) {
  const ep = Endpoints.GetUserVerification(userId);
  const { data: resp, isLoading, reloadData } = usePerformRequest<any>({
    method: ep.method,
    route: ep.route,
  });
  const v = resp?.data;
  const [busy, setBusy] = useState<string | null>(null);

  const err = (e: any) =>
    notifications.show({
      title: "Error",
      message: e?.response?.data?.message || e?.message || "Request failed",
      color: "red",
    });

  const setOverride = async (override: string) => {
    setBusy(override);
    try {
      const e = Endpoints.SetUserOverride(userId);
      await axios.post(e.route, { override }, authHeaders());
      notifications.show({
        title: "Override set",
        message: `Member → ${override}`,
        color: "green",
      });
      reloadData?.();
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };

  const recompute = async () => {
    setBusy("recompute");
    try {
      const e = Endpoints.RecomputeTier(userId);
      await axios.post(e.route, {}, authHeaders());
      notifications.show({
        title: "Recomputed",
        message: "Tier recalculated from socials",
        color: "blue",
      });
      reloadData?.();
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };

  if (!userId) return null;

  return (
    <Paper p="lg" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <IconShieldCheck size={18} />
          <Text fw={700}>Verification &amp; Tier</Text>
        </Group>
        <Group gap="xs">
          <Tooltip label="Recalculate tier from this member's social verdicts">
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconRefresh size={14} />}
              onClick={recompute}
              loading={busy === "recompute"}
            >
              Recompute
            </Button>
          </Tooltip>
        </Group>
      </Group>

      {isLoading ? (
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      ) : !v ? (
        <Text size="sm" c="dimmed">
          No verification data for this member.
        </Text>
      ) : (
        <Stack gap="sm">
          <Group gap="lg">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Tier
              </Text>
              <Badge color={tierColor(v.tier)} variant="light" size="lg">
                {v.tier || "unverified"}
              </Badge>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Admin override
              </Text>
              <Badge color={overrideColor(v.override)} variant="light" size="lg">
                {v.override || "none"}
              </Badge>
            </div>
          </Group>

          <Divider label="Linked socials" labelPosition="left" />
          {(!v.socials || v.socials.length === 0) && (
            <Text size="sm" c="dimmed">
              No social accounts linked.
            </Text>
          )}
          {v.socials?.map((s: any, i: number) => (
            <Group key={i} justify="space-between" wrap="nowrap">
              <div>
                <Text size="sm" fw={500}>
                  {s.platform} · {s.handle || "—"}
                </Text>
                <Group gap={6} mt={2}>
                  {s.followers != null && (
                    <Text size="xs" c="dimmed">
                      {s.followers} followers
                    </Text>
                  )}
                  {(s.flags || []).map((f: string, j: number) => (
                    <Badge key={j} size="xs" color="red" variant="light">
                      {f}
                    </Badge>
                  ))}
                </Group>
              </div>
              <Badge color={statusColor(s.status)} variant="light">
                {s.status}
              </Badge>
            </Group>
          ))}

          <Divider label="Override this member" labelPosition="left" />
          <Group gap="xs">
            <Button
              size="xs"
              color="green"
              variant="light"
              leftSection={<IconShieldCheck size={14} />}
              onClick={() => setOverride("force_verified")}
              loading={busy === "force_verified"}
            >
              Verified
            </Button>
            <Button
              size="xs"
              color="yellow"
              variant="light"
              leftSection={<IconMusic size={14} />}
              onClick={() => setOverride("force_streaming")}
              loading={busy === "force_streaming"}
            >
              Streaming
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={<IconBan size={14} />}
              onClick={() => setOverride("banned")}
              loading={busy === "banned"}
            >
              Ban
            </Button>
            <Button
              size="xs"
              color="gray"
              variant="light"
              leftSection={<IconRotate size={14} />}
              onClick={() => setOverride("none")}
              loading={busy === "none"}
            >
              Clear override
            </Button>
          </Group>
        </Stack>
      )}
    </Paper>
  );
}

/**
 * Permission-gated wrapper — only admins with MANAGE_VERIFICATION see (and
 * fetch) this panel. Keeps the mount-time GET from firing for admins who
 * couldn't use it anyway.
 */
export default function MemberVerificationControl({ userId }: Props) {
  return (
    <PermissionGuard permission={AdminPermissions.MANAGE_VERIFICATION}>
      <MemberVerificationInner userId={userId} />
    </PermissionGuard>
  );
}
